import React from "react";
import { FaIdBadge, FaWallet, FaCoins, FaBan } from "react-icons/fa";
import Switch from "../../atoms/Switch";

/**
 * Çalışan ödeme makinesi kartı (#391) — açıkken bu makineden paralı ürünü
 * yalnız firmanın çalışanları alabilir. Alt anahtarlar (#410) çalışanın hangi
 * kaynaklarla ödeyebileceğini belirler; yalnız kilit açıkken uygulanır.
 * Yalnız müşteri firmasındaki cihazda, `devices.employeeOnlyPayment` izniyle
 * gösterilir (karar InfoTab'da).
 * Sözleşme: BayinerIOT-Workspace- docs/api-contracts/employee-only-payment-device.md
 *
 * @param {boolean} enabled
 * @param {boolean} [allowWallet=true]
 * @param {boolean} [allowOkkalira=true]
 * @param {boolean} [saving]
 * @param {(enabled: boolean) => void} onToggle
 * @param {(field: "employeeOnlyAllowWallet"|"employeeOnlyAllowOkkalira", value: boolean) => void} [onSourceToggle]
 * @param {number} [rejections7d] - son 7 gün ret sayısı (izin yoksa verilmez)
 */
export default function EmployeeOnlyPaymentCard({
  enabled,
  allowWallet = true,
  allowOkkalira = true,
  saving,
  onToggle,
  onSourceToggle,
  rejections7d,
}) {
  const sources = [
    {
      field: "employeeOnlyAllowWallet",
      icon: FaWallet,
      label: "Kişisel cüzdanla ödeme",
      hint: "Kapalıyken çalışan yalnız firma kotasıyla alabilir.",
      value: allowWallet,
    },
    {
      field: "employeeOnlyAllowOkkalira",
      icon: FaCoins,
      label: "Okkalira kullanımı",
      hint: "Kapalıyken uygulamada Okkalira seçeneği görünmez.",
      value: allowOkkalira,
    },
  ];

  return (
    <div className="rounded-xl bg-white border border-gray-200 shadow-sm sm:hover:shadow-md transition-shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="bg-orange-500 text-white p-1.5 rounded-lg shadow-sm">
            <FaIdBadge className="w-4 h-4" />
          </div>
          <span className="text-sm font-SemiBold text-gray-800">Çalışan Ödeme Makinesi</span>
        </div>
        <Switch
          checked={!!enabled}
          onChange={onToggle}
          disabled={saving}
          label="Çalışan ödeme makinesi"
        />
      </div>
      <div className="px-4 py-3 space-y-1">
        <p className="text-[13px] font-SemiBold text-gray-900">
          {enabled ? "Yalnız firma çalışanları" : "Herkese açık"}
        </p>
        <p className="text-xs text-gray-500">
          Açıkken bu makineden paralı ürünü yalnız firmanın çalışanları alabilir.
        </p>
      </div>
      {onSourceToggle && (
        <div className="px-4 pb-1 border-t border-gray-100">
          {sources.map(({ field, icon: Icon, label, hint, value }) => (
            <div
              key={field}
              className={`flex items-center justify-between gap-3 py-2.5 border-b border-gray-100 last:border-b-0 ${
                enabled ? "" : "opacity-50"
              }`}
            >
              <div className="flex items-start gap-2 min-w-0">
                <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-Medium text-gray-800">{label}</p>
                  <p className="text-[11px] text-gray-500">{hint}</p>
                </div>
              </div>
              <Switch
                size="sm"
                checked={value !== false}
                onChange={(next) => onSourceToggle(field, next)}
                disabled={saving || !enabled}
                label={label}
              />
            </div>
          ))}
          {!enabled && (
            <p className="text-[11px] text-gray-400 pb-2">
              Bu ayarlar yalnız çalışan ödeme makinesi açıkken uygulanır.
            </p>
          )}
        </div>
      )}
      {Number.isFinite(rejections7d) && (
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50/60 border-t border-gray-100 text-xs text-gray-600">
          <FaBan className={`w-3 h-3 ${rejections7d > 0 ? "text-red-500" : "text-gray-300"}`} />
          <span>
            Son 7 gün: <span className="font-SemiBold">{rejections7d}</span> reddedilen ödeme
            {rejections7d > 0 && " · ayrıntı Ödeme Retleri sekmesinde"}
          </span>
        </div>
      )}
    </div>
  );
}
